/**
 * Avatar: la foto de una persona, o sus iniciales sobre un color estable.
 *
 * Es el ÚNICO sitio de la app que pinta un avatar. Antes había cinco, cada uno
 * con su propia regla de iniciales (`charAt(0)` con criterios distintos) y
 * todos con el mismo color para todo el mundo.
 *
 * Decisiones que no son obvias al leer el JSX:
 *
 *   · `onError` cae a las iniciales. Una foto borrada del bucket (o una fila
 *     que apunta a un objeto que ya no está) debe verse como «sin foto», no
 *     como un icono roto.
 *   · `alt=""` por defecto: en casi todas las pantallas el nombre está escrito
 *     justo al lado, así que anunciarlo otra vez es ruido para un lector de
 *     pantalla. Cuando el avatar va SOLO, se pasa `alt` explícito.
 *   · El color sale del `userId`, no del nombre — ver `avatarPaletteIndex`.
 */
import { type Component, createMemo, createSignal, Show } from "solid-js";
import { avatarPaletteIndex, avatarUrl, cn, initialsOf } from "@famidex/shared";
import { SERVER_URL } from "@/lib/runtime-config";

/**
 * Origen de la API por defecto.
 *
 * El componente lo resuelve solo para que los ~5 sitios que pintan un avatar no
 * tengan que acordarse de pasarlo (y para que ninguno se equivoque de origen).
 * `apiBaseUrl` sigue existiendo como prop para los tests y para escenarios
 * multi-origen.
 */
const apiBaseUrl = () => SERVER_URL;

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";

/** Diámetro y tamaño de letra por talla. */
const SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-10 w-10 text-sm",
  lg: "h-12 w-12 text-base",
  xl: "h-24 w-24 text-2xl",
};

export interface AvatarProps {
  /** Id de la persona: de aquí sale el COLOR (estable aunque cambie el nombre). */
  userId?: string | null;
  /** Nombre para mostrar; de aquí salen las iniciales. */
  name?: string | null;
  /** Email de reserva cuando no hay nombre. */
  email?: string | null;
  /** Clave de storage (`avatars/…`) o URL absoluta. Sin esto, iniciales. */
  image?: string | null;
  /** Origen de la API para construir la URL desde la clave. */
  apiBaseUrl?: string | null;
  size?: AvatarSize;
  /** Texto alternativo. Vacío = decorativo (el nombre ya está al lado). */
  alt?: string;
  class?: string;
  /** `data-testid` del contenedor. */
  testId?: string;
}

export const Avatar: Component<AvatarProps> = (props) => {
  // Se guarda la URL que falló, no un booleano: así, cuando el usuario cambia
  // de foto, el estado de error no se queda pegado de la imagen anterior (que
  // es lo que pasaría con un `failed: boolean` sin efecto que lo reinicie).
  const [failedSrc, setFailedSrc] = createSignal<string | null>(null);

  const size = () => props.size ?? "md";
  const src = createMemo(() => avatarUrl(props.image, props.apiBaseUrl ?? apiBaseUrl()));
  const initials = createMemo(() => initialsOf(props.name, props.email));
  const tone = createMemo(() => avatarPaletteIndex(props.userId));
  const usableSrc = createMemo(() => {
    const url = src();
    return url && url !== failedSrc() ? url : null;
  });

  return (
    <div
      class={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold select-none",
        SIZE_CLASSES[size()],
        // El tono solo se aplica cuando se pintan las iniciales; detrás de una
        // foto opaca sería invisible, pero se mantiene para que el hueco tenga
        // color mientras la imagen carga.
        "avatar-tone",
        `avatar-tone-${tone()}`,
        props.class,
      )}
      data-testid={props.testId}
      data-avatar-tone={tone()}
    >
      <Show when={usableSrc()} fallback={<span aria-hidden="true">{initials()}</span>}>
        {(url) => (
          <img
            src={url()}
            alt={props.alt ?? ""}
            class="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
            onError={() => setFailedSrc(url())}
            data-testid={props.testId ? `${props.testId}-image` : undefined}
          />
        )}
      </Show>
    </div>
  );
};
